import type { Point, PointArray } from './types/contour-definition-array-type';

import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import z from 'zod';

import { useCurrentStudyStore } from '../dicom-web/current-study-store';
import { getRoundContour } from './contour-processing/getRoundContour';
import { getScaleContour } from './contour-processing/getScaleContour';
import {
  gaussianSmoothing,
  movingAverageSmoothing,
} from './contour-processing/getSmoothContour';
import { PointArraySchema } from './types/contour-definition-array-type';

// enum contour types
export enum ContourType {
  PolygonClosedContour = 'PolygonClosedContour',
  PolygonOpenContour = 'PolygonOpenContour',
  PointContour = 'PointContour',
  LineContour = 'LineContour',
  AnnotationTextContour = 'AnnotationTextContour',
  AnnotationArrowContour = 'AnnotationArrowContour',
  None = 'None',
}

// enum contour labels
export enum ContourLabel {
  SaxLvEndo = 'SaxLvEndo',
  SaxLvEndoTrabecular = 'SaxLvEndoTrabecular',
  SaxLvEpi = 'SaxLvEpi',
  SaxLvOpenEpi = 'SaxLvOpenEpi',
  SaxLvPapillary = 'SaxLvPapillary',
  SaxRvEndo = 'SaxRvEndo',
  SaxRvPapillary = 'SaxRvPapillary',
  SaxLvExclusion = 'SaxLvExclusion',
  SaxLvMyoFusion = 'SaxLvMyoFusion',
  SaxLvTransmurality = 'SaxLvTransmurality',
  SaxLvChord = 'SaxLvChord',
  SaxLvCenterChord = 'SaxLvCenterChord',
  SaxLvSegment = 'SaxLvSegment',
  LaxLvEndo = 'LaxLvEndo',
  LaxLvEndoBasalPoint = 'LaxLvEndoBasalPoint',
  LaxLvEpi = 'LaxLvEpi',
  LaxLvExtendEndo = 'LaxLvExtendEndo',
  LaxLvExtendEpi = 'LaxLvExtendEpi',
  LaxLvMyoFusion = 'LaxLvMyoFusion',
  LaxLvTransmurality = 'LaxLvTransmurality',
  LaxLvChord = 'LaxLvChord',
  LaxLvCenterChord = 'LaxLvCenterChord',
  LaxLvSegment = 'LaxLvSegment',
  LaxLvRange = 'LaxLvRange',
  RvInsertion = 'RvInsertion',
  FlowRoi1 = 'FlowRoi1',
  FlowRoi2 = 'FlowRoi2',
  FlowStaticTissueRoi = 'FlowStaticTissueRoi',
  GeneralPolygon = 'GeneralPolygon',
  GeneralLine = 'GeneralLine',
  MaskMeasurement = 'MaskMeasurement',
  AnnotationText = 'AnnotationText',
  AnnotationArrow = 'AnnotationArrow',
  None = 'None',
}

export const ContourLabelOrder = {
  SaxLvEndo: 1,
  SaxLvEpi: 1,
  SaxRvEndo: 2,
  RvInsertion: 3,
  MaskMeasurement: 4,
} as const;

// ToDo: consistent color and icon definition, either with contour definition or separate
export const ContourLabelColor = {
  SaxLvEndo: '#d60000',
  SaxLvEpi: 'green',
  SaxLvPapillary: 'magenta',
  SaxRvEndo: 'yellow',
  RvInsertion: '#0035f5',

  SaxRvPapillary: 'orange',
  SaxLvExclusion: 'blue',
  SaxLvEndoTrabecular: 'red',

  GeneralPolygon: '#2E3CFF',
  GeneralLine: 'darkorange',
  MaskMeasurement: 'white',
  AnnotationText: 'white',
  AnnotationArrow: '#6DE66D',

  LaxLvEndo: 'red',
  LaxLvEndoBasalPoint: 'red',
  LaxLvEpi: 'green',
} as const;

export const ContourLabelColorSelected = {
  SaxLvEndo: '#ff0000',
  SaxLvEpi: '#0ecf00',
  SaxLvPapillary: '#FF5CC6',
  SaxRvEndo: '#FCFF5C',
  RvInsertion: '#00d0ff',

  SaxRvPapillary: '#FFB65C',
  SaxLvEndoTrabecular: '#FA1616',

  GeneralPolygon: '#5C67FF',
  GeneralLine: 'darkorange',
  MaskMeasurement: 'white',
  AnnotationText: 'white',
  AnnotationArrow: '#25DA25',

  LaxLvEndo: 'red',
  LaxLvEndoBasalPoint: 'red',
  LaxLvEpi: 'green',
} as const;

export const ContourLabelToIcon: Partial<Record<ContourLabel, string>> = {
  [ContourLabel.SaxLvEndo]: '/icons/sax_endo_icon_draft.svg',
  [ContourLabel.SaxLvEpi]: '/icons/sax_epi_icon_draft.svg',
  [ContourLabel.RvInsertion]: '/icons/rvinsertion_icon_draft.svg',
  [ContourLabel.SaxRvEndo]: '/icons/sax_rvendo_icon_draft.svg',
  [ContourLabel.LaxLvEndo]: '/icons/lax_endo_icon_draft.svg',
  [ContourLabel.LaxLvEpi]: '/icons/lax_epi_icon_draft.svg',
  [ContourLabel.GeneralPolygon]: '/icons/freehand_icon_draft.svg',
  [ContourLabel.GeneralLine]: '/icons/line_icon_draft.svg',
  [ContourLabel.MaskMeasurement]: '/icons/maskmeasure_icon_draft.svg',
  [ContourLabel.AnnotationText]: '/icons/text_annotation_draft.svg',
  [ContourLabel.AnnotationArrow]: '/icons/arrow_annotation_draft.svg',
  [ContourLabel.FlowRoi1]: '/icons/flow_roi1_draft.svg',
  [ContourLabel.FlowRoi2]: '/icons/flow_roi2_draft.svg',
  [ContourLabel.FlowStaticTissueRoi]: '/icons/flow_roi_static_draft.svg',
  [ContourLabel.LaxLvRange]: '/icons/lax_lv_range_draft.svg',
  [ContourLabel.SaxLvOpenEpi]: '/icons/lax_epi_icon_draft.svg',
  [ContourLabel.SaxLvPapillary]: '/icons/sax_lv_pap_draft.svg',
  [ContourLabel.SaxLvExclusion]: '/icons/sax_lv_exclusion_draft.svg',
  [ContourLabel.SaxRvPapillary]: '/icons/sax_rv_pap_draft.svg',

  // ... add more mappings as needed
};

export const ContourLabelToMiniIcon: Partial<Record<ContourLabel, string>> = {
  [ContourLabel.SaxLvEndo]: '/icons/contour/sax_endo_icon_mini.svg',
  [ContourLabel.SaxLvEpi]: '/icons/contour/sax_epi_icon_mini.svg',
  [ContourLabel.RvInsertion]: '/icons/contour/rvinsertion_icon_mini.svg',
  [ContourLabel.SaxRvEndo]: '/icons/contour/sax_rvendo_icon_mini.svg',
  [ContourLabel.LaxLvEndo]: '/icons/contour/lax_endo_icon_mini.svg',
  [ContourLabel.LaxLvEpi]: '/icons/contour/lax_epi_icon_mini.svg',
  [ContourLabel.GeneralPolygon]: '/icons/contour/freehand_icon_mini.svg',
  [ContourLabel.GeneralLine]: '/icons/contour/line_icon_mini.svg',
  [ContourLabel.MaskMeasurement]: '/icons/maskmeasure_icon_draft.svg',
  [ContourLabel.AnnotationText]: '/icons/contour/text_icon_mini_draft.svg',
  [ContourLabel.AnnotationArrow]: '/icons/contour/arrow_min_draft.svg',
  // Flow ROIs reuse the freehand mini badge until dedicated icons exist.
  [ContourLabel.FlowRoi1]: '/icons/flow_roi1_draft.svg',
  [ContourLabel.FlowRoi2]: '/icons/flow_roi2_draft.svg',
  [ContourLabel.FlowStaticTissueRoi]: '/icons/flow_roi_static_draft.svg',
  [ContourLabel.LaxLvRange]: '/icons/lax_lv_range_draft.svg',
  [ContourLabel.SaxLvOpenEpi]: '/icons/lax_epi_icon_draft.svg',
  [ContourLabel.SaxLvPapillary]: '/icons/sax_lv_pap_draft.svg',
  [ContourLabel.SaxLvExclusion]: '/icons/sax_lv_exclusion_draft.svg',
  [ContourLabel.SaxRvPapillary]: '/icons/sax_rv_pap_draft.svg',

};

// contour ids
/** An opaque "compound ID" string like "1-2-3" */
export const ContourLabelItemIdSchema = z.string().brand('ContourLabelItemId');
export type ContourLabelItemId = z.infer<typeof ContourLabelItemIdSchema>;

/**
 * Encode an array of numeric IDs into a single string.
 * e.g. [5]        → "5"
 *      [5, 10]    → "5-10"
 *      [5, 10, 3] → "5-10-3"
 */
export function encodeContourLabelItemId(ids: number[]): ContourLabelItemId {
  if (ids.length === 0) {
    return '-1' as ContourLabelItemId;
  }
  return ids.join('-') as ContourLabelItemId;
}
/**
 * Decode a string back into an array of numbers.
 * Splits on "-" and parses each segment as an integer.
 */
export function decodeContourLabelItemId(s: ContourLabelItemId): number[] {
  if (!s) {
    return [];
  }
  return s.split('-').map((piece) => {
    const n = Number(piece);
    if (Number.isNaN(n)) {
      throw new TypeError(`Invalid ID segment "${piece}"`);
    }
    return n;
  });
}
/**
 * Runtime guard
 */
export function isContourLabelItemId(s: string): s is ContourLabelItemId {
  return /^\d+(?:-\d+)*$/.test(s);
}

export interface ContourId {
  label: ContourLabel;
  id: ContourLabelItemId;
}

// export function createContourId(params: Partial<ContourId> = {}): ContourId {
//   return {
//     label: params.label ?? ContourLabel.None,
//     id: params.id ?? (encodeContourLabelItemId([]) as ContourLabelItemId)
//   };
// }

export function isPoint(obj: unknown): obj is Point {
  return (
    obj !== null
    && typeof obj === 'object'
    && 'x' in obj
    && typeof obj.x === 'number'
    && 'y' in obj
    && typeof obj.y === 'number'
  );
}

export function equalContourIds(
  a: ContourId | null | undefined,
  b: ContourId | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  return a.label === b.label && a.id === b.id;
}

// define contour
export const ContourPropsSchema = z.object({
  type: z.enum(ContourType).readonly(),
  label: z.enum(ContourLabel).readonly(),
  id: z.string().brand('ContourLabelItemId'),
  imageUID: z.string(),
  frameOfReferenceUID: z.string(),
  displayLabel: z.string(),
  color: z.string(),
  multiplicity: z.number(),
  userEditable: z.boolean().readonly(),
  dependendGeneratedContours: z.array(z.enum(ContourLabel)).readonly(),
  icon: z.string(),
  text: z.string().optional(),
});

export type ContourProps = z.infer<typeof ContourPropsSchema>;

export const ContourSerializedDataSchema = z.object({
  props: ContourPropsSchema,
  polygon: PointArraySchema,
});

export type ContourSerializedData = z.infer<typeof ContourSerializedDataSchema>;

export const ContourDefinitions: Record<ContourLabel, ContourProps>
  = Object.freeze({
    [ContourLabel.None]: Object.freeze({
      type: ContourType.None,
      label: ContourLabel.None,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'None',
      color: 'white',
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.SaxLvEndo]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvEndo,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Endo',
      color: ContourLabelColor.SaxLvEndo,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.SaxLvTransmurality,
        ContourLabel.SaxLvMyoFusion,
        ContourLabel.SaxLvChord,
        ContourLabel.SaxLvCenterChord,
        ContourLabel.SaxLvSegment,
      ],
      icon: '/icons/sax_endo_icon_draft.svg',
    }),
    [ContourLabel.SaxLvEndoTrabecular]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvEndoTrabecular,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Endo Trabecular',
      color: ContourLabelColor.SaxLvEndoTrabecular,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.SaxLvTransmurality,
        ContourLabel.SaxLvMyoFusion,
        ContourLabel.SaxLvChord,
        ContourLabel.SaxLvCenterChord,
        ContourLabel.SaxLvSegment,
      ],
      icon: '/icons/sax_endo_icon_draft.svg',
    }),
    [ContourLabel.SaxLvEpi]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvEpi,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Epi',
      color: ContourLabelColor.SaxLvEpi,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.SaxLvTransmurality,
        ContourLabel.SaxLvMyoFusion,
        ContourLabel.SaxLvChord,
        ContourLabel.SaxLvCenterChord,
        ContourLabel.SaxLvSegment,
      ],
      icon: '/icons/sax_epi_icon_draft.svg',
    }),
    [ContourLabel.SaxLvOpenEpi]: Object.freeze({
      type: ContourType.PolygonOpenContour,
      label: ContourLabel.SaxLvOpenEpi,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Open Epi',
      color: ContourLabelColor.SaxLvEpi,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.SaxLvMyoFusion,
      ],
      icon: '/icons/lax_epi_icon_draft.svg',
    }),
    [ContourLabel.SaxLvPapillary]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvPapillary,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Papillary',
      color: ContourLabelColor.SaxLvPapillary,
      multiplicity: 25,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.SaxLvExclusion]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvExclusion,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax LV Exclusion',
      color: ContourLabelColor.SaxLvExclusion,
      multiplicity: 5,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.SaxRvEndo]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxRvEndo,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax RV Endo',
      color: ContourLabelColor.SaxRvEndo,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '/icons/sax_rvendo_icon_draft.svg',
    }),
    [ContourLabel.SaxRvPapillary]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxRvPapillary,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax RV Papillary',
      color: ContourLabelColor.SaxRvPapillary,
      multiplicity: 25,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.RvInsertion]: Object.freeze({
      type: ContourType.PointContour,
      label: ContourLabel.RvInsertion,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'RV Insertion',
      color: ContourLabelColor.RvInsertion,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.SaxLvChord,
        ContourLabel.SaxLvCenterChord,
        ContourLabel.SaxLvSegment,
      ],
      icon: '/icons/rvinsertion_icon_draft.svg',
    }),
    [ContourLabel.SaxLvMyoFusion]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvMyoFusion,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax Lv Fusion',
      color: 'white',
      multiplicity: 100,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.SaxLvTransmurality]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvTransmurality,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax Lv Transmurality',
      color: 'white',
      multiplicity: 40,
      userEditable: false,
      dependendGeneratedContours: [
        ContourLabel.SaxLvMyoFusion,
        ContourLabel.SaxLvChord,
        ContourLabel.SaxLvCenterChord,
        ContourLabel.SaxLvSegment,
      ],
      icon: '',
    }),
    [ContourLabel.SaxLvChord]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.SaxLvChord,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax Lv Chord',
      color: 'white',
      multiplicity: 200,
      userEditable: false,
      dependendGeneratedContours: [ContourLabel.SaxLvSegment],
      icon: '',
    }),
    [ContourLabel.SaxLvCenterChord]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.SaxLvCenterChord,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax Lv Center Chord',
      color: 'white',
      multiplicity: 200,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.SaxLvSegment]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.SaxLvSegment,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Sax Lv Segment',
      color: 'white',
      multiplicity: 1000,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvEndo]: Object.freeze({
      type: ContourType.PolygonOpenContour,
      label: ContourLabel.LaxLvEndo,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax LV Endo',
      color: ContourLabelColor.LaxLvEndo,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.LaxLvExtendEndo,
        ContourLabel.LaxLvTransmurality,
        ContourLabel.LaxLvMyoFusion,
        ContourLabel.LaxLvChord,
        ContourLabel.LaxLvCenterChord,
        ContourLabel.LaxLvSegment,
      ],
      icon: '/icons/lax_endo_icon_draft.svg',
    }),
    [ContourLabel.LaxLvEndoBasalPoint]: Object.freeze({
      type: ContourType.PointContour,
      label: ContourLabel.LaxLvEndoBasalPoint,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax LV Endo Basal Point',
      color: ContourLabelColor.LaxLvEndoBasalPoint,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvEpi]: Object.freeze({
      type: ContourType.PolygonOpenContour,
      label: ContourLabel.LaxLvEpi,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax LV Epi',
      color: ContourLabelColor.LaxLvEpi,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [
        ContourLabel.LaxLvExtendEpi,
        ContourLabel.LaxLvTransmurality,
        ContourLabel.LaxLvMyoFusion,
        ContourLabel.LaxLvChord,
        ContourLabel.LaxLvCenterChord,
        ContourLabel.LaxLvSegment,
      ],
      icon: '/icons/lax_epi_icon_draft.svg',
    }),
    [ContourLabel.LaxLvExtendEndo]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.LaxLvExtendEndo,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Extend Endo',
      color: 'white',
      multiplicity: 1,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvExtendEpi]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.LaxLvExtendEpi,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Extend Epi',
      color: 'white',
      multiplicity: 1,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvMyoFusion]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.LaxLvMyoFusion,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Myo Fusion',
      color: 'white',
      multiplicity: 1,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvTransmurality]: Object.freeze({
      type: ContourType.PolygonOpenContour,
      label: ContourLabel.LaxLvTransmurality,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Transmurality',
      color: 'white',
      multiplicity: 25,
      userEditable: false,
      dependendGeneratedContours: [
        ContourLabel.LaxLvMyoFusion,
        ContourLabel.LaxLvChord,
        ContourLabel.LaxLvSegment,
      ],
      icon: '',
    }),
    [ContourLabel.LaxLvChord]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.LaxLvChord,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Chord',
      color: 'white',
      multiplicity: 1,
      userEditable: false,
      dependendGeneratedContours: [ContourLabel.LaxLvSegment],
      icon: '',
    }),
    [ContourLabel.LaxLvCenterChord]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.LaxLvCenterChord,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Center Chord',
      color: 'white',
      multiplicity: 200,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvSegment]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.LaxLvSegment,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Segment',
      color: 'white',
      multiplicity: 1000,
      userEditable: false,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.LaxLvRange]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.LaxLvRange,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Lax Lv Range',
      color: 'magenta',
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.FlowRoi1]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.FlowRoi1,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Flow ROI 1',
      color: 'blue',
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.FlowRoi2]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.FlowRoi2,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Flow ROI 2',
      color: 'magenta',
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.FlowStaticTissueRoi]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.FlowStaticTissueRoi,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Flow Static Tissue ROI',
      color: 'red',
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '',
    }),
    [ContourLabel.GeneralPolygon]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.GeneralPolygon,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Freehand',
      color: ContourLabelColor.GeneralPolygon,
      multiplicity: 10,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '/icons/freehand_icon_draft.svg',
    }),
    [ContourLabel.GeneralLine]: Object.freeze({
      type: ContourType.LineContour,
      label: ContourLabel.GeneralLine,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Distance',
      color: ContourLabelColor.GeneralLine,
      multiplicity: 10,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '/icons/line_icon_draft.svg',
    }),
    [ContourLabel.MaskMeasurement]: Object.freeze({
      type: ContourType.PolygonClosedContour,
      label: ContourLabel.MaskMeasurement,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Mask Measurement',
      color: ContourLabelColor.MaskMeasurement,
      multiplicity: 1,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: ContourLabelToIcon[ContourLabel.MaskMeasurement] ?? '',
    }),
    [ContourLabel.AnnotationText]: Object.freeze({
      type: ContourType.AnnotationTextContour,
      label: ContourLabel.AnnotationText,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Annotation Text',
      color: ContourLabelColor.AnnotationText,
      multiplicity: 10,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '/icons/text_annotation_draft.svg',
    }),
    [ContourLabel.AnnotationArrow]: Object.freeze({
      type: ContourType.AnnotationArrowContour,
      label: ContourLabel.AnnotationArrow,
      id: encodeContourLabelItemId([-1]),
      imageUID: '',
      frameOfReferenceUID: '',
      displayLabel: 'Annotation Arrow',
      color: ContourLabelColor.AnnotationArrow,
      multiplicity: 10,
      userEditable: true,
      dependendGeneratedContours: [],
      icon: '/icons/arrow_annotation_draft.svg',
    }),
  });

// implementations
export abstract class ContourBase {
  protected props: ContourProps = {
    type: ContourType.None,
    label: ContourLabel.None,
    id: encodeContourLabelItemId([-1]),
    imageUID: '',
    frameOfReferenceUID: '',
    displayLabel: 'label',
    color: '#FFFFFF',
    multiplicity: 1,
    userEditable: true,
    dependendGeneratedContours: [],
    icon: '',
  };

  protected _polygon: PointArray = [];

  public static readonly subMatrix: number = 4;

  protected constructor(
    props: Partial<ContourProps> & Pick<ContourProps, 'type' | 'label'>,
  ) {
    this.props = { ...this.props, ...props };
  }

  protected getVtKImageData(): vtkImageData | undefined {
    const image = useCurrentStudyStore().getVtkImageData(this.props.imageUID);
    if (!image) {
      return undefined;
    }
    const scalars = image.getPointData()?.getScalars();
    if (!scalars || !scalars.getData() || scalars.getData().length === 0) {
      return undefined;
    }
    // ToDo transformation needed?
    return image;
  }

  public getImageWidth(): number | undefined {
    if (!this.props.imageUID) return undefined;
    const imageWidth
      = useCurrentStudyStore().imageInfoDicom[this.props.imageUID]?.Columns;
    if (!imageWidth) {
      console.error('ContourBase - getImageWidth - imageWidth undefined');
      return 0;
    }
    return imageWidth;
  }

  public getImageHeight(): number | undefined {
    if (!this.props.imageUID) return undefined;
    const imageHeight
      = useCurrentStudyStore().imageInfoDicom[this.props.imageUID]?.Rows;
    if (!imageHeight) {
      console.error('ContourBase - getImageHeight - imageHeight undefined');
      return 0;
    }
    return imageHeight;
  }

  public getProps(): ContourProps {
    return this.props;
  }

  protected getPixelSignalIntensity(
    // ToDo: fix this
    imageData: vtkImageData | undefined,
    point: Point,
  ): number | undefined {
    if (!imageData) {
      return undefined;
    }
    const scalars = imageData.getPointData()?.getScalars();
    const data = scalars?.getData();
    if (!data || data.length === 0) {
      return undefined;
    }

    const dims = imageData.getDimensions();
    if (!dims || dims[0] <= 0 || dims[1] <= 0) {
      return undefined;
    }

    const px = Math.round(point.x);
    const py = Math.round(point.y);
    if (px < 0 || px >= dims[0] || py < 0 || py >= dims[1]) {
      return undefined;
    }

    const idx = py * dims[0] + px;
    if (idx < 0 || idx >= data.length) {
      return undefined;
    }

    const rawSi = data[idx];
    if (rawSi == null || Number.isNaN(rawSi)) {
      return undefined;
    }

    let scale = 1.0;
    const isVencImage2D
      = useCurrentStudyStore().imageInfoDicom[this.props.imageUID]?.IsVencImage2D;
    if (isVencImage2D) {
      scale
        = useCurrentStudyStore().imageInfoDicom[this.props.imageUID]?.VencScale2D ?? 1.0;
    }

    return rawSi * scale;
  }

  // DICOM first value is row spacing, second is column spacing
  public getPixelSpacing(): [number, number] {
    const spacing
      = useCurrentStudyStore().imageInfoDicom[this.props.imageUID]?.PixelSpacing;
    if (!spacing) {
      console.error('ContourBase - getPixelSpacing - spacing undefined');
      return [0.0, 0.0];
    }
    return [spacing[0], spacing[1]];
  }

  public getPolygon(): PointArray {
    return this._polygon;
  }

  abstract isValid(): void;
  abstract setPolygon(polygon: PointArray): void;
  abstract setPolygonScaled(polygon: PointArray): void;
  abstract getPolygonScaled(): PointArray;
  abstract getCentroid(): Point;
  abstract getCentroidScaled(): Point;

  public translate(distancePoint: [number, number]) {
    const dx = Math.round(distancePoint[0] * ContourBase.subMatrix);
    const dy = Math.round(distancePoint[1] * ContourBase.subMatrix);
    const polygon = this.getPolygon().map((point: { x: number; y: number }) => ({
      x: point.x + dx,
      y: point.y + dy,
    }));
    this.setPolygon(polygon);
  }

  public smoothContour(method = 'gaussian', strength = 1.5): void {
    const polygon = this.getPolygonScaled();
    let smoothedPolygon: PointArray;
    if (method === 'movingAverage') {
      smoothedPolygon = movingAverageSmoothing(polygon, strength);
    }
    else {
      smoothedPolygon = gaussianSmoothing(polygon, strength);
    }
    this.setPolygonScaled(smoothedPolygon);
  }

  public roundContour(): void {
    const polygon = this.getPolygonScaled();
    const centroid = this.getCentroidScaled();
    const roundedPolygon = getRoundContour(polygon, centroid);
    this.setPolygonScaled(roundedPolygon);
  }

  public scaleContour(percentChange: number = 5): void {
    const polygon = this.getPolygonScaled();
    const centroid = this.getCentroidScaled();
    const scaledPolygon = getScaleContour(polygon, centroid, percentChange);
    this.setPolygonScaled(scaledPolygon);
  }

  public growContour(percent: number = 5): void {
    this.scaleContour(Math.abs(percent));
  }

  public shrinkContour(percent: number = 5): void {
    this.scaleContour(-Math.abs(percent));
  }

  /**
   * Serializes the contour to a JSON-compatible object
   */
  public serialize(): ContourSerializedData {
    return {
      props: this.props,
      polygon: this._polygon,
    };
  }

  /**
   * Updates the contour state from serialized data
   */
  public deserialize(data: ContourSerializedData): void {
    this.props = { ...this.props, ...data.props };
    this._polygon = [...data.polygon];
  }
}
